import { Component, OnInit, OnDestroy, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { map, takeUntil, first, startWith, distinctUntilChanged } from 'rxjs/operators';
import { AlgOtpModalComponent } from '../../components/dialogs/alg-otp-modal/alg-otp-modal.component';
import 'moment/locale/it';
import { ModalDialogService } from 'ngx-modal-dialog';
import { UtilsService } from '../../services/utils.service';
import { AlgConditionModalComponent } from '../../components/dialogs/alg-condition-modal/alg-condition-modal.component';

import { AlgConsensoService, REQUEST_STATUS_INVALID, REQUEST_STATUS_OK } from '../../services/alg-consenso.service';
import { ApprovalFeedback } from '../../services/alg-consenso.service.d';
import { LocationStrategy } from '@angular/common';
import { CambioPiano } from '../../models/fisso';
import { VulnerabilityType } from '../../models/product';

@Component({
  selector: 'alg-change-plan',
  templateUrl: './alg-change-plan-component.html',
  styleUrls: ['./alg-change-plan-component.scss'],
})
export class AlgChangePlanComponent implements OnInit, OnDestroy {
  public isLoading$: Observable<boolean>;
  public productType$: Observable<string>;
  public operatorRating$: Observable<ApprovalFeedback>;
  public isConsentsOk: boolean;
  public uploadedDocumentFront: boolean;
  public uploadedDocumentBack: boolean;
  public checkbox104Checked: boolean;
  public checkboxDocsReceptionChecked: boolean;
  public checkboxSupplyStartChecked: boolean;
  public checkboxGeneralConditionChecked: boolean;
  public feedbackOptions = [
    { image: 'bad', value: 100 },
    { image: 'normal', value: 200 },
    { image: 'good', value: 300 },
    { image: 'excellent', value: 400 },
  ];
  public feedbackModalContent = {
    title: 'La tua privacy per noi è importante.',
    description: '',
  };
  public product: any;
  public isLight: boolean;
  public isGas: boolean;

  public idRichiesta: string;
  public typeRichiesta: string;
  public codeRichiesta: string;
  public temporaryUrl: string;
  public isOtpChecked: boolean;
  public goNextStep: boolean;
  public changePlan: CambioPiano = {} as CambioPiano;
  public urlExpired: boolean;
  public secondStep: boolean;
  public creationDateOfRequest: any;

  private opportunityId$: Observable<any>;
  private ngUnsubscribe = new Subject<void>();

  // OBSERVABLE TO WATCH REQUEST STATUS
  public isRequestOk$: Observable<boolean>;
  public isErrorGeneric$: Observable<boolean>;

  public checkboxGeneralCondition = {
    checkboxConsents: false,
  };

  public controlGasDeclare: boolean;
  public controlGasInformative: boolean;
  public controlLightDeclare: boolean;
  public controlLightInformative: boolean;
  public invalidGeneralCondition = {
    consents: false,
    clause: false,
  };
  public invalid104Condition: boolean;
  public consentsWithAdditionalProperty: any;
  public cambioPiano: CambioPiano = {} as CambioPiano;
  public isProductGasVulnerable: boolean;
  public isVulnerabilityTypeDisability: boolean;

  private _id: string;
  private month = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  public needsPreConsent: boolean = true;

  constructor(
    private activatedRoute: ActivatedRoute,
    private consensoService: AlgConsensoService,
    private utils: UtilsService,
    private modalDialogService: ModalDialogService,
    private viewContainer: ViewContainerRef,
    public locations: LocationStrategy
  ) {
    this.opportunityId$ = this.activatedRoute.params.pipe(map((params) => params && params.id));
    this.isLoading$ = this.consensoService.isLoading$;

    this.productType$ = this.consensoService.approvalProduct$.pipe(map((product) => !!product && product.type));
    this.operatorRating$ = this.consensoService.operatorRatingCode$.pipe(first((rating) => !!rating));
    this.consensoService.uploaded104DocumentFront$.subscribe((value) => {
      this.uploadedDocumentFront = value;
      if (value) {
        this.getValid104Checkbox(value);
      }
    });
    this.consensoService.uploaded104DocumentBack$.subscribe((value) => {
      this.uploadedDocumentBack = value;
      if (value) {
        this.getValid104Checkbox(value);
      }
    });
    this.consensoService.checkbox104Checked$.subscribe((value) => {
      this.checkbox104Checked = value;
    });
    this.consensoService.checkboxDocsReception$.subscribe((value) => {
      this.checkboxDocsReceptionChecked = value;
    });
    this.consensoService.checkboxSupplyStart$.subscribe((value) => {
      this.checkboxSupplyStartChecked = value;
    });
    this.consensoService.checkboxGeneralCondition$.subscribe((value) => {
      this.checkboxGeneralConditionChecked = value;
    });
  }

  public ngOnInit() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.temporaryUrl = window.location.href;
      this.idRichiesta = params.id;
      this.codeRichiesta = params.codice;
      this.typeRichiesta = this.temporaryUrl.includes('Luce&Gas') ? 'Luce&Gas' : params.tipo;
    });

    this.changePlan.requestId = this.idRichiesta;

    this.consensoService.fetchChangePlanRequest(this.idRichiesta, this.codeRichiesta, this.typeRichiesta);

    this.isRequestOk$ = this.consensoService.changePlanRequestStatus$.pipe(
      map((request) => request === REQUEST_STATUS_OK),
      startWith(false),
      distinctUntilChanged()
    );

    this.isErrorGeneric$ = this.consensoService.changePlanRequestStatus$.pipe(
      map((request) => request === REQUEST_STATUS_INVALID),
      startWith(false),
      distinctUntilChanged()
    );

    this.consensoService.changePlanRequest$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((request) => {
      if (!request) return;
      this.changePlan = request;
      // If vulnerabilityType is equal to 'Età anagrafica', then the user is not vulnerable
      this.isProductGasVulnerable =
        this.changePlan.NewProductName === 'ALG Vulnerabile Gas Domestico' && this.changePlan.vulnerabilityType !== VulnerabilityType.anagraficAge
          ? true
          : false;

      this.isVulnerabilityTypeDisability = this.changePlan.vulnerabilityType === VulnerabilityType.disability ? true : false;

      this.consensoService.isVulnerabilityTypeDisability$.next(this.changePlan.vulnerabilityType === VulnerabilityType.disability ? true : false);

      this.consensoService.vulnerabilityType$.next(this.changePlan.vulnerabilityType);
      if (!!this.changePlan.DataDecorrenza) {
        this.changePlan.DataDecorrenza = this.invertDateFormat(this.changePlan.DataDecorrenza);
      }

      this.urlExpired =
        (request.canaleProvenienza.toUpperCase() === 'SMS' || request.canaleProvenienza.toUpperCase() === 'EMAIL') && !request.otpValidateRequest
          ? true
          : false;

      const monthCte = parseInt(request.dataCTE.substring(5, 8));
      const yearCte = parseInt(request.dataCTE.substring(0, 4));

      this.changePlan.monthOfCTE = this.month[monthCte - 1];
      this.changePlan.yearOfCTE = yearCte;

      if (this.changePlan.POD) {
        this.changePlan.isLight = !this.changePlan.isLight;
      }

      if (this.changePlan.PDR) {
        this.changePlan.isGas = !this.changePlan.isGas;
      }

      this.changePlan.isDual = this.changePlan.isGas && this.changePlan.isLight;

      this.isOtpChecked = request.Alg_OTPCheck__c;
      this.needsPreConsent = !request.Alg_OTPCheck__c && !this.urlExpired;

      if (this.changePlan.vulnerabilityType === 'Disabilità') {
        this.changePlan.vulnerabilityType104Disability = true;
      }
    });

    this.consensoService.isSecontStepFissoRequest$.subscribe((step) => {
      this.secondStep = step;
    });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public openModal(modalContent) {
    this.utils.disableOverflow();
    this.modalDialogService.openDialog(this.viewContainer, {
      title: modalContent.title,
      childComponent: AlgConditionModalComponent,
      settings: {
        closeButtonClass: 'close theme-icon-close',
        headerTitleClass: ' modal-title modal-title-underline',
      },
      data: {
        description: modalContent.description,
      },
    });
  }

  public readCondition(type) {
    let modalContent;
    if (type === 'dataConsent') {
      modalContent = this.feedbackModalContent;
    }
    this.openModal(modalContent || {});
  }

  // feedback
  public onFeebackModal(event) {
    if (event === 'open') {
      this.readCondition('dataConsent');
    }
  }

  public openOTPmodal() {
    if (!this.goNextStep) {
      if (!this.checkboxGeneralCondition.checkboxConsents) {
        this.invalidGeneralCondition.consents = true;
        this.scrollOnInvalidElement('generalInvalid');
      }
      if (this.isProductGasVulnerable && this.invalid104Condition) {
        this.scrollOnInvalidElement('104Invalid');
      }
      return;
    }
    this.utils.disableOverflow();
    if (this.goNextStep) {
      this.consentsWithAdditionalProperty = {
        requestId: `${this.changePlan.requestId}`,
        requestCode: `${this.codeRichiesta}`,
        requestType: `${this.typeRichiesta}`,
        Alg_OTPCheck__c: false,
        Alg_OTPCheckDate__c: '',
        Alg_OTPPhone__c: '',
        phone: `${this.changePlan.phone}`,
        vulnerabilityType104Disability: this.changePlan.vulnerabilityType104Disability,
      };
      this.isConsentsOk = true;

      this.modalDialogService.openDialog(this.viewContainer, {
        title: 'Un ultimo passo: verifica il tuo numero di cellulare',
        childComponent: AlgOtpModalComponent,
        settings: {
          closeButtonClass: 'close theme-icon-close',
          headerTitleClass: 'modal-title modal-title-underline textCenter',
        },
        data: {
          isConsentsOk: this.isConsentsOk,
          consents: this.consentsWithAdditionalProperty,
          phone: this.changePlan.phone,
          linear: false,
          consensoType: 'change_plan_request',
        },
      });
    }
  }

  private scrollOnInvalidElement(element) {
    document.getElementById(element).scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
    });
  }

  // Function to set valid OTP Verify button - General Condition Right Section
  public getValidCheckboxesSecondPart(value: boolean) {
    this.checkboxGeneralCondition.checkboxConsents = value;
    this.invalidGeneralCondition.consents = !value;

    if (this.isProductGasVulnerable && this.isVulnerabilityTypeDisability) {
      return (this.goNextStep = !this.invalid104Condition && this.uploadedDocumentFront && this.uploadedDocumentBack && value);
    } else {
      return (this.goNextStep = value);
    }
  }

  // Function to set valid OTP Verify button - 104 Condition Section
  public getValid104Checkbox(value: boolean) {
    this.invalid104Condition = !value;
    if (this.checkboxGeneralCondition.checkboxConsents && this.uploadedDocumentFront && this.uploadedDocumentBack) {
      return (this.goNextStep = value);
    }
  }

  // Convert date format
  private invertDateFormat(date) {
    return date.split('-').reverse().join('-');
  }

  public handlePreConsentClick() {
    this.needsPreConsent = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/alg-change-plan/alg-change-plan-component.ts